import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const syncUser = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try { setCurrentUser(JSON.parse(storedUser)); }
        catch (e) { setCurrentUser(null); }
      } else {
        setCurrentUser(null);
      }
    };
    syncUser();
    window.addEventListener("storage", syncUser);
    window.addEventListener("authChange", syncUser);
    return () => {
      window.removeEventListener("storage", syncUser);
      window.removeEventListener("authChange", syncUser);
    };
  }, []);

  const quickLinks = [
    { key: "navHome",      action: () => navigate(currentUser ? "/home" : "/") },
    { key: "navAbout",     action: () => navigate("/about") },
    { key: "navBooking",   action: () => navigate(currentUser ? "/booking" : "/login") },
    { key: "navDonations", action: () => navigate(currentUser ? "/donations" : "/login") },
    { key: "ftFestival",   action: () => {
        navigate("/");
        setTimeout(() => {
          const el = document.querySelector(".fest-section");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
    }},
    { key: "ftContactUs",  action: () => navigate("/contact") },
  ];

  const vazhipaduKeys = [
    "ftNivedyam",
    "ftPushpanjali",
    "ftArchana",
    "ftDeeparadhana",
    "ftAshtottaram"
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

        :root {
          --gold-bright: #e8c46a;
          --gold-mid:    #c9973a;
          --gold-dim:    #8a6020;
          --gold-pale:   #f5e4b0;
        }

        /* ── Root ── */
        .footer-root {
          font-family: 'Cinzel', serif;
          width: 100%;
          overflow: hidden;
          background:
            radial-gradient(ellipse at 50% 0%, rgba(109,20,20,0.45) 0%, transparent 55%),
            linear-gradient(180deg, #120202 0%, #0e0101 60%, #080101 100%);
        }

        /* ── Top strip ── */
        .footer-top-strip {
          height: 3px;
          background: linear-gradient(90deg,
            transparent 0%, var(--gold-dim) 10%, var(--gold-bright) 30%,
            #fff8e7 50%, var(--gold-bright) 70%, var(--gold-dim) 90%, transparent 100%);
          position: relative;
        }
        .footer-top-strip::before,
        .footer-top-strip::after {
          content: '✦';
          position: absolute; top: 50%; transform: translateY(-50%);
          color: var(--gold-bright); font-size: 10px;
        }
        .footer-top-strip::before { left: 8%; }
        .footer-top-strip::after  { right: 8%; }

        /* ── Hero block ── */
        .footer-hero {
          text-align: center;
          padding: 52px 24px 36px;
        }

        .footer-rule {
          display: flex;
          align-items: center;
          max-width: 600px;
          margin: 0 auto 32px;
        }
        .footer-rule-line {
          flex: 1; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(232,196,106,0.4));
        }
        .footer-rule-line.right {
          background: linear-gradient(90deg, rgba(232,196,106,0.4), transparent);
        }
        .footer-rule-jewel {
          width: 48px; height: 48px; flex-shrink: 0;
          position: relative;
          display: flex; align-items: center; justify-content: center;
        }
        .footer-rule-ring {
          position: absolute; inset: 0; border-radius: 50%;
          background: conic-gradient(
            from 0deg,
            transparent 0deg, var(--gold-bright) 60deg,
            transparent 120deg, var(--gold-mid) 180deg,
            transparent 240deg, var(--gold-bright) 300deg,
            transparent 360deg
          ) border-box;
          border: 1.5px solid transparent;
          -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: destination-out;
          mask-composite: exclude;
          animation: footerSpin 10s linear infinite;
        }
        @keyframes footerSpin { to { transform: rotate(360deg); } }

        .footer-rule-om {
          font-family: 'Cinzel Decorative', cursive; font-size: 18px;
          background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          position: relative; z-index: 1;
        }

        .footer-temple-name {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 22px; font-weight: 900;
          letter-spacing: 2.5px; text-transform: uppercase;
          background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 35%, var(--gold-mid) 65%, #b87a28 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          line-height: 1.3; margin-bottom: 6px;
        }
        .footer-temple-sub {
          font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600;
          letter-spacing: 2.5px; text-transform: uppercase;
          color: rgba(232,196,106,0.5); margin-bottom: 4px;
        }
        .footer-temple-loc {
          font-family: 'EB Garamond', serif; font-style: italic;
          font-size: 14px; color: rgba(232,196,106,0.4);
          letter-spacing: 1px; margin-bottom: 22px;
        }
        .footer-tagline {
          font-family: 'EB Garamond', serif; font-style: italic;
          font-size: 15px; color: rgba(245,228,176,0.5);
          max-width: 440px; margin: 0 auto;
          line-height: 1.7; letter-spacing: 0.3px;
        }

        /* ── Divider ── */
        .footer-divider {
          height: 1px; margin: 0 40px;
          background: linear-gradient(90deg,
            transparent 0%, rgba(232,196,106,0.08) 15%,
            rgba(232,196,106,0.25) 50%,
            rgba(232,196,106,0.08) 85%, transparent 100%);
        }

        /* ── 3-col grid ── */
        .footer-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          padding: 44px 60px;
        }

        .footer-col {
          padding: 0 28px;
          position: relative;
        }
        .footer-col:not(:last-child)::after {
          content: '';
          position: absolute; right: 0; top: 10%; height: 80%; width: 1px;
          background: linear-gradient(180deg,
            transparent, rgba(232,196,106,0.2) 30%,
            rgba(232,196,106,0.2) 70%, transparent);
        }

        .footer-col-title {
          font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700;
          letter-spacing: 3px; text-transform: uppercase;
          color: var(--gold-mid); margin-bottom: 6px;
          display: flex; align-items: center; gap: 10px;
        }
        .footer-col-title::after {
          content: ''; flex: 1; height: 1px;
          background: linear-gradient(90deg, rgba(232,196,106,0.3), transparent);
        }

        .footer-col-heading {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 13px; font-weight: 700; letter-spacing: 1px;
          background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          margin-bottom: 20px; line-height: 1.3;
        }

        /* ── Links ── */
        .footer-links { list-style: none; display: flex; flex-direction: column; gap: 2px; }

        .footer-link {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 0;
          color: rgba(245,228,176,0.55);
          font-family: 'Cinzel', serif; font-size: 11px; font-weight: 500;
          letter-spacing: 1.2px; text-transform: uppercase;
          cursor: pointer; transition: all 0.3s ease;
          border: none; border-bottom: 1px solid rgba(232,196,106,0.06);
          background: none; width: 100%; text-align: left;
        }
        .footer-link-arrow {
          width: 14px; height: 1px;
          background: var(--gold-dim);
          position: relative; flex-shrink: 0; transition: all 0.3s ease;
        }
        .footer-link-arrow::after {
          content: ''; position: absolute; right: 0; top: -3px;
          width: 6px; height: 6px;
          border-top: 1px solid var(--gold-dim); border-right: 1px solid var(--gold-dim);
          transform: rotate(45deg); transition: all 0.3s ease;
        }
        .footer-link:hover { color: var(--gold-pale); padding-left: 6px; }
        .footer-link:hover .footer-link-arrow {
          width: 20px; background: var(--gold-bright);
          box-shadow: 0 0 6px var(--gold-bright);
        }
        .footer-link:hover .footer-link-arrow::after { border-color: var(--gold-bright); }

        /* ── Contact ── */
        .contact-block { display: flex; flex-direction: column; gap: 14px; }

        .contact-item { display: flex; gap: 12px; align-items: flex-start; }

        .contact-icon {
          width: 32px; height: 32px; flex-shrink: 0;
          border: 1px solid rgba(232,196,106,0.2);
          background: rgba(232,196,106,0.05);
          display: flex; align-items: center; justify-content: center;
          font-size: 13px;
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
          transition: background 0.3s;
        }
        .contact-item:hover .contact-icon { background: rgba(232,196,106,0.12); }

        .contact-label {
          font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700;
          letter-spacing: 2px; text-transform: uppercase;
          color: var(--gold-dim); display: block; margin-bottom: 2px;
        }
        .contact-value {
          font-family: 'EB Garamond', serif;
          font-size: 14px; color: rgba(245,228,176,0.7);
          letter-spacing: 0.3px; line-height: 1.4;
        }

        /* ── Social ── */
        .footer-social { display: flex; gap: 10px; margin-top: 20px; }

        .social-btn {
          width: 36px; height: 36px;
          background: rgba(0,0,0,0.3); color: rgba(232,196,106,0.5);
          font-size: 14px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
          border: none; transition: all 0.3s ease;
        }
        .social-btn:hover {
          background: rgba(232,196,106,0.15);
          color: var(--gold-bright); transform: scale(1.12);
        }

        /* ── Bottom bar ── */
        .footer-bottom {
          border-top: 1px solid rgba(232,196,106,0.1);
          padding: 32px 60px;
          display: grid; 
          grid-template-columns: 1fr auto 1fr;
          align-items: center; gap: 24px;
          background: linear-gradient(180deg, rgba(232,196,106,0.02) 0%, transparent 100%);
          position: relative;
        }
        .footer-tag {
          font-family: 'EB Garamond', serif; font-style: italic;
          font-size: 13.5px; color: rgba(232,196,106,0.45); letter-spacing: 0.5px;
          text-align: right;
        }
        @media (max-width: 768px) {
          .footer-bottom { 
            grid-template-columns: 1fr; 
            text-align: center; 
            gap: 12px;
            padding: 24px 20px;
          }
          .footer-tag { text-align: center; }
          .footer-copy, .footer-tag { font-size: 12px; }
          .footer-credit { font-size: 13px; }
        }
        @media (max-width: 480px) {
          .footer-bottom { padding: 20px 16px; gap: 10px; }
          .footer-copy, .footer-tag { font-size: 11px; }
          .footer-credit { font-size: 12px; }
        }
        .footer-bottom::before {
          content: ''; position: absolute; top: -1px; left: 50%; width: 40%; height: 1px;
          background: radial-gradient(circle, rgba(232,196,106,0.4) 0%, transparent 70%);
          transform: translateX(-50%);
        }
        .footer-copy {
          font-family: 'EB Garamond', serif; font-style: italic;
          font-size: 13.5px; color: rgba(232,196,106,0.45); letter-spacing: 0.5px;
          text-shadow: 0 0 10px rgba(0,0,0,0.5);
        }
        .footer-credit {
          font-family: 'EB Garamond', serif; font-style: italic;
          font-size: 14px; font-weight: 500; letter-spacing: 0.8px;
          background: linear-gradient(90deg, #fffaf0, var(--gold-bright), var(--gold-mid), var(--gold-bright), #fffaf0);
          background-size: 200% auto;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 5px rgba(232,196,106,0.2));
          animation: shimmer 6s linear infinite;
          cursor: pointer;
        }
        @keyframes shimmer {
          to { background-position: 200% center; }
        }
        .footer-credit:hover {
          filter: drop-shadow(0 0 10px rgba(232,196,106,0.5));
          transform: scale(1.02);
        }
        .footer-bottom-links { display: flex; gap: 20px; align-items: center; }
        .footer-bottom-link {
          font-family: 'Cinzel', serif; font-size: 9px; font-weight: 600;
          letter-spacing: 1.5px; text-transform: uppercase;
          color: rgba(232,196,106,0.3); cursor: pointer;
          transition: color 0.3s; background: none; border: none; padding: 0;
        }
        .footer-bottom-link:hover { color: rgba(232,196,106,0.65); }
        .footer-bottom-sep {
          width: 3px; height: 3px;
          background: rgba(232,196,106,0.2); transform: rotate(45deg); flex-shrink: 0;
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .footer-grid { grid-template-columns: 1fr 1fr; padding: 36px 32px; gap: 36px 0; }
          .footer-col:not(:last-child)::after { display: none; }
          .footer-col { padding: 0 16px; }
          .footer-col:nth-child(3) {
            grid-column: 1 / -1;
            border-top: 1px solid rgba(232,196,106,0.08); padding-top: 28px;
          }
          .footer-bottom { padding: 18px 32px; flex-direction: column; text-align: center; gap: 10px; }
          .footer-divider { margin: 0 24px; }
          .footer-temple-name { font-size: 18px; }
        }

        @media (max-width: 600px) {
          .footer-grid { grid-template-columns: 1fr; padding: 28px 20px; gap: 28px; }
          .footer-col:nth-child(3) { grid-column: auto; }
          .footer-col { padding: 0; border-top: 1px solid rgba(232,196,106,0.08); padding-top: 24px; }
          .footer-col:first-child { border-top: none; padding-top: 0; }
          .footer-hero { padding: 36px 20px 28px; }
          .footer-temple-name { font-size: 16px; letter-spacing: 1.5px; }
          .footer-temple-sub { font-size: 9.5px; letter-spacing: 1.5px; }
          .footer-tagline { font-size: 13px; }
          .footer-divider { margin: 0 16px; }
          .footer-bottom { padding: 16px 20px; }
          .footer-bottom-links { flex-wrap: wrap; justify-content: center; }
        }

        @media (max-width: 380px) {
          .footer-temple-name { font-size: 14px; }
          .footer-col-heading { font-size: 12px; }
          .footer-link { font-size: 10px; }
        }
      `}</style>

      <footer className="footer-root">

        <div className="footer-top-strip" />

        {/* Hero */}
        <div className="footer-hero">
          <div className="footer-rule">
            <div className="footer-rule-line" />
            <div className="footer-rule-jewel">
              <div className="footer-rule-ring" />
              <span className="footer-rule-om">ॐ</span>
            </div>
            <div className="footer-rule-line right" />
          </div>
          
          {/* 7️⃣ Use Translations */}
          <div className="footer-temple-name">{t("ftTempleName")}</div>
          <div className="footer-temple-name" style={{ marginTop: "-4px" }}>{t("ftTempleSub")}</div>
          <div className="footer-temple-sub" style={{ marginTop: "8px" }}>✦ {t("ftTempleLoc")} ✦</div>
          <div className="footer-temple-loc">{t("ftEstablished")}</div>
          <p className="footer-tagline">
            "{t("ftTagline")}"
          </p>
        </div>

        <div className="footer-divider" />

        {/* Grid */}
        <div className="footer-grid">

          {/* Col 1 — Quick Links */}
          <div className="footer-col">
            <div className="footer-col-title">{t("ftNavigate")}</div>
            <div className="footer-col-heading">{t("ftQuickLinks")}</div>
            <ul className="footer-links">
              {quickLinks.map(({ key, action }) => (
                <li key={key}>
                  <button className="footer-link" onClick={action}>
                    <span className="footer-link-arrow" />
                    {t(key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 2 — Vazhipadu */}
          <div className="footer-col">
            <div className="footer-col-title">{t("ftOfferings")}</div>
            <div className="footer-col-heading">{t("ftVazhipadu")}</div>
            <ul className="footer-links">
              {vazhipaduKeys.map((key) => (
                <li key={key}>
                  <button className="footer-link">
                    <span className="footer-link-arrow" />
                    {t(key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Contact */}
          <div className="footer-col">
            <div className="footer-col-title">{t("ftReachUs")}</div>
            <div className="footer-col-heading">{t("ftContactLoc")}</div>
            <div className="contact-block">
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <span className="contact-label">{t("ftAddress")}</span>
                  <span className="contact-value">
                    {t("ftTempleName")} {t("ftTempleSub")},<br />
                    Pandanad, Chengannur,<br />
                    Alappuzha District, Kerala – 689506
                  </span>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div>
                  <span className="contact-label">{t("ftPhone")}</span>
                  <span className="contact-value">+91 94470 14544</span>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div>
                  <span className="contact-label">{t("ftEmail")}</span>
                  <span className="contact-value">kavilsreebhadrakalidevitemple@gmail.com</span>
                </div>
              </div>
              <div className="contact-item">
          
              </div>
            </div>
            <div className="footer-social">
              {["f", "𝕏", "▶", "📸"].map((icon, i) => (
                <button className="social-btn" key={i}>{icon}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="footer-divider" />

        {/* Bottom bar */}
        <div className="footer-bottom">
          <span className="footer-copy">
            {t("ftCopy")}
          </span>
          <span className="footer-credit">
            {t("ftCredit")}
          </span>
          <span className="footer-tag">
            {t("ftTag")}
          </span>
        </div>

      </footer>
    </>
  );
}

export default Footer;

